import axios from 'axios'
import { debounce } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import type { SupplierFilters, SupplierRow, SupplierTab } from './SupplierList.types'
import { mapApiDataToRows } from './helpers'

export function useSupplierData(activeTab: SupplierTab) {
  const [rows, setRows] = useState<SupplierRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<SupplierFilters>({
    supplier: '',
    oem: '',
    type: '',
    blanket: false,
    status: 'approved',
  })
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [totalCount, setTotalCount] = useState(0)

  useEffect(() => {
    setFilters(prev => ({ ...prev, status: activeTab }))
  }, [activeTab])

  async function load() {
    if (activeTab === 'oemsPending') return
    setLoading(true)
    setError(null)

    try {
      const { data } = await axios.get<{
        count: number
        list: {
          id: string
          name: string
          type: string
          blanket: boolean
          documentsLabel: string
          documentsUrl?: string
          authorizedOems: string[]
          status: SupplierTab
          defaultNetsuiteVendor: string | null
        }[]
      }>('/supplier', {
        params: {
          ...filters,
          page,
          pageSize,
        },
      })

      setRows(mapApiDataToRows(data.list))
      setTotalCount(data.count)
    } catch (err) {
      console.error(err)
      setError('Failed to load suppliers')
    } finally {
      setLoading(false)
    }
  }

  const loadRef = useRef(load)
  loadRef.current = load
  const debouncedLoad = useRef(debounce(() => loadRef.current(), 500)).current

  useEffect(() => {
    load()
  }, [page, pageSize, filters.status])

  useEffect(() => {
    debouncedLoad()
  }, [filters.blanket, filters.supplier, filters.oem, filters.type])

  return { rows, setRows, loading, error, filters, setFilters, page, setPage, pageSize, setPageSize, totalCount }
}
